// Convocatoria · Wizard de 3 pasos (Tipo/Fecha → Ubicaciones → Revisión).
// -----------------------------------------------------------------------------
// Reglas UX:
//   · Paso 1 elige tipo y fecha. Muestra guard si el alumno tiene penalización.
//   · Paso 2 elige centro y punto de encuentro desde catálogo (con "+ Nuevo").
//   · Paso 3 muestra revisión + botón Programar.
//   · En prácticos: bloqueo profesor+vehículo + rango horario editable.
// -----------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CaptureGuideDialog from "./CaptureGuideDialog";
import ExamHistoryPanel from "./ExamHistoryPanel";
import ExamRequestLogisticsDialog from "./ExamRequestLogisticsDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ChevronRight,
  ChevronLeft,
  Trophy,
  BookOpen,
  Car,
  Plus,
  MapPin,
  Camera,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  X,
  Pencil,
  Trash2,
} from "lucide-react";

const TABS = ["Tipo y fecha", "Ubicaciones", "Revisión"];

// Iniciales a partir del nombre del profesor (ej. "MARIA GARCIA" → "MG").
function teacherInitials(name) {
  return (name || "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

function CatalogPicker({ label, items, valueId, onSelectId, onCreate, testid, showPhoto }) {
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", address: "", maps_url: "", photo: "" });
  const [saving, setSaving] = useState(false);
  // V1.41 · Guía de encuadre previa a la cámara nativa (nueva ubicación).
  const [guideOpen, setGuideOpen] = useState(false);
  const photoInputRef = useRef(null);

  const handlePhoto = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Foto máx. 500 KB");
    const reader = new FileReader();
    reader.onload = () => setNewItem((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!newItem.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const created = await onCreate(newItem);
      onSelectId(created.id);
      setCreating(false);
      setNewItem({ name: "", address: "", maps_url: "", photo: "" });
    } finally { setSaving(false); }
  };

  const selected = items.find((x) => x.id === valueId);

  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{label}</label>
      <div className="mt-1 flex gap-2">
        <select
          value={valueId}
          onChange={(e) => onSelectId(e.target.value)}
          data-testid={testid}
          className="flex-1 py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none">
          <option value="">— Selecciona —</option>
          {items.map((it) => (
            <option key={it.id} value={it.id}>{it.name}</option>
          ))}
        </select>
        <button type="button" onClick={() => setCreating((v) => !v)}
          data-testid={`${testid}-new-btn`}
          className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold rounded-lg border border-neutral-300 hover:bg-neutral-50">
          {creating ? <><X className="h-3.5 w-3.5" /> Cancelar</> : <><Plus className="h-3.5 w-3.5" /> Nuevo</>}
        </button>
      </div>

      {selected && !creating && (
        <div className="mt-2 rounded-lg border border-neutral-200 bg-neutral-50 p-2 flex items-start gap-2 text-[12px]">
          {selected.photo && (
            <img src={selected.photo} alt="" className="w-16 h-16 object-cover rounded-md" />
          )}
          <div className="min-w-0 flex-1">
            {selected.address && <div className="text-neutral-700">{selected.address}</div>}
            {selected.maps_url && (
              <a href={selected.maps_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3" /> Ver en Maps
              </a>
            )}
          </div>
        </div>
      )}

      {creating && (
        <div className="mt-2 rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/40 p-3 space-y-2">
          <input type="text" placeholder="Nombre" value={newItem.name}
            onChange={(e) => setNewItem((s) => ({ ...s, name: e.target.value }))}
            data-testid={`${testid}-new-name`}
            className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300" />
          {showPhoto && (
            <input type="text" placeholder="Dirección" value={newItem.address}
              onChange={(e) => setNewItem((s) => ({ ...s, address: e.target.value }))}
              className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300" />
          )}
          {/* Los puntos de encuentro comparten catálogo con las clases prácticas
              (`/locations`), que sólo guarda el nombre: no se piden foto ni Maps. */}
          {showPhoto && (
          <input type="text" placeholder="URL Google Maps (https://maps.app.goo.gl/…)" value={newItem.maps_url}
            onChange={(e) => setNewItem((s) => ({ ...s, maps_url: e.target.value }))}
            className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300" />
          )}
          {showPhoto && (
          <div className="flex items-center gap-2">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handlePhoto(e.target.files?.[0])}
              className="hidden"
              data-testid="conv-location-photo-input"
            />
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="conv-location-photo-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-neutral-300 hover:bg-neutral-50"
            >
              <Camera className="h-3.5 w-3.5" /> Añadir foto
            </button>
            {newItem.photo && <img src={newItem.photo} alt="" className="h-10 w-10 rounded-md object-cover" />}
          </div>
          )}
          <CaptureGuideDialog
            open={guideOpen}
            docType="ubicacion"
            onCancel={() => setGuideOpen(false)}
            onContinue={() => { setGuideOpen(false); photoInputRef.current?.click(); }}
          />
          <button type="button" onClick={submit} disabled={saving}
            data-testid={`${testid}-new-save`}
            className="w-full inline-flex items-center justify-center gap-1 py-1.5 text-xs font-semibold rounded-md bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Guardar y seleccionar
          </button>
        </div>
      )}
    </div>
  );
}

export default function ConvocatoriaWizard({ open, onClose, student, onCreated }) {
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState("teorico");
  const [form, setForm] = useState({
    date: "", time: "",
    hora_maxima_jefatura: "",
    hora_punto_encuentro: "",
    notes: "",
    practico_block_start: "07:00",
    practico_block_end: "15:00",
    teacher_initials: student?.teacher_initials || "",
    vehicle_id: "",
  });
  const [centerId, setCenterId] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [centers, setCenters] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState("");
  const [historic, setHistoric] = useState({
    enabled: false, approved_date: "", notes: "", center_name: "", expediente_dgt: "",
  });
  // V1.79 · Tasas del alumno (integradas aquí desde la antigua pestaña Solicitudes)
  const [tasas, setTasas] = useState([]);
  const [tasaId, setTasaId] = useState("");
  const [addingTasa, setAddingTasa] = useState(false);
  const [savingTasa, setSavingTasa] = useState(false);
  const [newTasa, setNewTasa] = useState({ tasas_number: "", presented_date: "" });
  const [tasaDetail, setTasaDetail] = useState(null);
  const [editTasa, setEditTasa] = useState(null);

  const saveTasaEdit = async () => {
    setSavingTasa(true);
    try {
      await api.patch(`/exam-requests/${editTasa.id}`, {
        tasas_number: editTasa.tasas_number.trim(),
        presented_date: editTasa.presented_date || undefined,
      });
      setEditTasa(null);
      await loadTasas();
      toast.success("Tasa actualizada");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo actualizar la tasa");
    } finally { setSavingTasa(false); }
  };

  const deleteTasa = async (t) => {
    if (!window.confirm(`¿Eliminar la tasa ${t.tasas_number || ""}?\n\nSe pedirá el PIN maestro. No se puede eliminar si ya tiene exámenes asignados.`)) return;
    try {
      await api.delete(`/exam-requests/${t.id}`);
      if (tasaId === t.id) setTasaId("");
      await loadTasas();
      toast.success("Tasa eliminada");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo eliminar la tasa");
    }
  };

  const loadTasas = useCallback(async () => {
    if (!student?.id) return;
    try {
      const { data } = await api.get(`/students/${student.id}/tasas-unificadas`);
      setTasas(data || []);
      setTasaId((prev) => {
        if (prev && (data || []).some((t) => t.id === prev)) return prev;
        return (data || []).find((t) => t.id && !t.state?.exhausted)?.id || "";
      });
    } catch { /* silencio */ }
  }, [student?.id]);

  // Tasa del repositorio DGT sin solicitud: se vincula al elegirla.
  const pickTasa = async (t) => {
    if (t.id) { setTasaId(t.id); return; }
    try {
      const { data } = await api.post(`/students/${student.id}/exam-requests/adopt`, {
        tasas_number: t.tasas_number, presented_date: t.presented_date || "",
      });
      await loadTasas();
      setTasaId(data.id);
      toast.success("Tasa vinculada a la convocatoria");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo vincular la tasa");
    }
  };

  const createTasa = async () => {
    setSavingTasa(true);
    try {
      const { data } = await api.post(`/students/${student.id}/exam-requests`, {
        tasas_number: newTasa.tasas_number.trim(),
        presented_date: newTasa.presented_date || new Date().toISOString().slice(0, 10),
      });
      setTasas((s) => [data, ...s]);
      setTasaId(data.id);
      setAddingTasa(false);
      setNewTasa({ tasas_number: "", presented_date: "" });
      toast.success("Tasa añadida");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo añadir la tasa");
    } finally { setSavingTasa(false); }
  };

  useEffect(() => {
    if (!open) return;
    setStep(0); setKind("teorico"); setCenterId(""); setMeetingId(""); setPreview("");
    setHistoric({ enabled: false, approved_date: "", notes: "", center_name: "", expediente_dgt: "" });
    setForm((s) => ({ ...s, date: "", time: "", hora_maxima_jefatura: "", hora_punto_encuentro: "", notes: "" }));
    (async () => {
      try {
        const [c, m, v, t] = await Promise.all([
          api.get("/exam-centers"),
          // V1.78 · Mismo catálogo de puntos de encuentro que las clases prácticas.
          api.get("/locations"),
          api.get("/vehicles").catch(() => ({ data: [] })),
          // V1.38 · Fuente única de profesores = `/teachers`.
          api.get("/teachers", { params: { include_inactive: false } }).catch(() => ({ data: [] })),
        ]);
        setCenters(c.data || []);
        setMeetings(m.data || []);
        setVehicles(v.data || []);
        setTeachers(t.data || []);
      } catch { /* silencio */ }
      loadTasas();
    })();
  }, [open, loadTasas]);

  const createCenter = async (payload) => {
    const { data } = await api.post("/exam-centers", payload);
    setCenters((s) => [...s, data]);
    return data;
  };
  const createMeeting = async (payload) => {
    const { data } = await api.post("/locations", { name: payload.name });
    setMeetings((s) => [...s, data]);
    return data;
  };

  const penaltyBlock = (() => {
    const na = student?.next_exam_allowed_from;
    if (!na) return null;
    if (form.date && form.date < na) return na;
    return null;
  })();

  const isApto = !!student?.apto_date;

  const submitHistoric = async () => {
    if (!historic.approved_date) return toast.error("Indica la fecha de aprobación del teórico");
    setSubmitting(true);
    try {
      await api.post(`/students/${student.id}/theory-history`, {
        approved_date: historic.approved_date,
        notes: historic.notes,
        center_name: historic.center_name,
        expediente_dgt: historic.expediente_dgt,
      });
      toast.success("Registro histórico guardado · teórico marcado como aprobado");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar el registro histórico");
    } finally { setSubmitting(false); }
  };

  const step1Valid = form.date && form.time && !penaltyBlock && !isApto;
  const step2Valid = centerId && (kind === "teorico" || meetingId);

  const buildPreview = () => {
    const c = centers.find((x) => x.id === centerId) || {};
    const m = meetings.find((x) => x.id === meetingId) || null;
    const kindLabel = kind === "practico" ? "PRÁCTICO" : "TEÓRICO";
    const firstName = (student?.name || "").split(" ")[0] || "alumno/a";
    const lines = [
      "*CONFIRMADO*",
      form.date,
      `*Examen ${kindLabel}*`,
      "",
      `Hola ${firstName}, tu examen ha sido convocado.`,
      "",
      "Hora del examen:",
      `*${form.time}*`,
    ];
    if (form.hora_maxima_jefatura) {
      lines.push("", "Hora máxima en la jefatura:", form.hora_maxima_jefatura);
    }
    lines.push("", "Lugar del examen:", c.name || "", c.address || "", c.maps_url || "");
    if (kind === "practico" && m) {
      lines.push("", "Punto de encuentro:", m.name || "", m.maps_url || "");
      if (form.hora_punto_encuentro) lines.push(`Hora en el punto de encuentro: *${form.hora_punto_encuentro}*`);
    }
    lines.push("", "Por favor, acude con tu documentación (DNI, permiso provisional si aplica) y máxima puntualidad.",
      "", "— Natur Autoescuela");
    return lines.filter((l, i, arr) => !(l === "" && arr[i - 1] === "")).join("\n");
  };

  useEffect(() => {
    if (step === 2) {
      setPreview(buildPreview());
    }
  }, [step, centerId, meetingId, form.date, form.time, kind]);   

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        kind, date: form.date, time: form.time,
        hora_maxima_jefatura: form.hora_maxima_jefatura,
        hora_punto_encuentro: form.hora_punto_encuentro,
        center_id: centerId,
        meeting_point_id: meetingId || "",
        notes: form.notes,
        practico_block_start: form.practico_block_start,
        practico_block_end: form.practico_block_end,
        teacher_initials: form.teacher_initials,
        vehicle_id: form.vehicle_id,
        exam_request_id: tasaId || "",
      };
      await api.post(`/students/${student.id}/convocatorias`, payload);
      toast.success("Convocatoria creada · cita añadida a la agenda");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo crear la convocatoria");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="convocatoria-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-600" />
            Nueva convocatoria de examen
          </DialogTitle>
          <DialogDescription className="text-xs">
            {student?.name} · {student?.codigo_expediente || "sin expediente"}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 py-2 border-b border-neutral-200">
          {TABS.map((t, i) => (
            <div key={t} className="flex items-center gap-2 text-xs">
              <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full font-bold ${
                step === i ? "bg-orange-600 text-white" : step > i ? "bg-emerald-500 text-white" : "bg-neutral-200 text-neutral-600"
              }`}>{i + 1}</span>
              <span className={step === i ? "font-semibold" : "text-neutral-500"}>{t}</span>
              {i < TABS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-neutral-300" />}
            </div>
          ))}
        </div>

        {/* PASO 1 */}
        {step === 0 && (
          <div className="space-y-3 pt-2">
            {isApto && (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-800">
                Este alumno ya está APTO. El expediente está cerrado.
              </div>
            )}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Tipo de examen</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setKind("teorico")}
                  data-testid="wizard-kind-teorico"
                  className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 font-semibold text-sm ${
                    kind === "teorico" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-neutral-200 hover:bg-neutral-50"
                  }`}>
                  <BookOpen className="h-4 w-4" /> Teórico
                </button>
                <button type="button" onClick={() => setKind("practico")}
                  data-testid="wizard-kind-practico"
                  className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 font-semibold text-sm ${
                    kind === "practico" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-neutral-200 hover:bg-neutral-50"
                  }`}>
                  <Car className="h-4 w-4" /> Práctico
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Fecha</label>
                <input type="date" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))}
                  data-testid="wizard-date"
                  className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Hora examen</label>
                <input type="time" value={form.time} onChange={(e) => setForm((s) => ({ ...s, time: e.target.value }))}
                  data-testid="wizard-time"
                  className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Hora máx. Jefatura (opc.)</label>
                <input type="time" value={form.hora_maxima_jefatura} onChange={(e) => setForm((s) => ({ ...s, hora_maxima_jefatura: e.target.value }))}
                  className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 text-sm" />
              </div>
              {kind === "practico" && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Hora punto encuentro</label>
                  <input type="time" value={form.hora_punto_encuentro} onChange={(e) => setForm((s) => ({ ...s, hora_punto_encuentro: e.target.value }))}
                    className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 text-sm" />
                </div>
              )}
            </div>
            {penaltyBlock && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                Fecha bloqueada por penalización. Este alumno no puede examinarse antes del <b>{penaltyBlock}</b>.
              </div>
            )}
            {/* V1.79 · Tasas (antes pestaña "Solicitudes") */}
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="wizard-tasas">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
                  Tasas · derechos de examen
                </div>
                <button
                  type="button" onClick={() => setAddingTasa((v) => !v)}
                  data-testid="wizard-add-tasa-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-neutral-300 bg-white hover:bg-neutral-50"
                >
                  <Plus className="h-3 w-3" /> Añadir tasas
                </button>
              </div>

              {addingTasa && (
                <div className="rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/40 p-2 space-y-2">
                  <input
                    type="text" inputMode="numeric" placeholder="Nº de tasas (p. ej. 412524488018)"
                    value={newTasa.tasas_number}
                    onChange={(e) => setNewTasa((s) => ({ ...s, tasas_number: e.target.value }))}
                    data-testid="wizard-new-tasa-number"
                    className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300 font-mono"
                  />
                  <input
                    type="date" value={newTasa.presented_date}
                    onChange={(e) => setNewTasa((s) => ({ ...s, presented_date: e.target.value }))}
                    data-testid="wizard-new-tasa-date"
                    className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300"
                  />
                  <button
                    type="button" onClick={createTasa} disabled={savingTasa || !newTasa.tasas_number.trim()}
                    data-testid="wizard-new-tasa-save"
                    className="w-full inline-flex items-center justify-center gap-1 py-1.5 text-xs font-semibold rounded-md bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
                  >
                    {savingTasa ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Guardar y seleccionar
                  </button>
                </div>
              )}

              {tasas.length === 0 && !addingTasa && (
                <p className="text-[12px] text-amber-700 font-semibold" data-testid="wizard-no-tasa">
                  Este alumno no tiene ninguna tasa registrada. Puedes continuar sin tasa y
                  asignársela después desde el historial de exámenes.
                </p>
              )}
              {tasas.length > 0 && !tasaId && (
                <p className="text-[12px] text-amber-700 font-semibold" data-testid="wizard-tasa-unselected">
                  Sin tasa seleccionada: la convocatoria se creará sin consumir tasa y podrás
                  asignarla luego en el historial de exámenes.
                </p>
              )}

              {tasas.map((t) => (
                <div
                  key={t.id || `dgt:${t.dgt_tasa_id}`}
                  className={
                    "flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] " +
                    (tasaId === t.id ? "border-orange-400 bg-white" : "border-neutral-200 bg-white")
                  }
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio" name="tasa" checked={!!t.id && tasaId === t.id}
                      disabled={t.state?.exhausted}
                      onChange={() => pickTasa(t)}
                      data-testid={`wizard-tasa-${t.id || t.dgt_tasa_id}`}
                      className="h-3.5 w-3.5 accent-orange-600"
                    />
                    <button
                      type="button"
                      onClick={() => t.id && setTasaDetail(t)}
                      data-testid={`wizard-tasa-open-${t.id || t.dgt_tasa_id}`}
                      className="font-mono font-bold text-neutral-800 underline decoration-dotted hover:text-orange-700"
                      title="Ver la ficha de la tasa y su historial de exámenes"
                    >
                      {t.tasas_number || "(sin nº)"}
                    </button>
                  </label>
                  <span className="flex items-center gap-1.5">
                    <span className={
                      "px-1.5 py-0.5 rounded border text-[10px] font-bold " +
                      (t.state?.exhausted
                        ? "bg-red-100 text-red-800 border-red-300"
                        : "bg-emerald-100 text-emerald-800 border-emerald-300")
                    }>
                      {t.state?.label || "sin usar"}
                    </span>
                    {t.id && (
                      <>
                        <button
                          type="button" title="Editar nº de tasa y fecha"
                          data-testid={`wizard-tasa-edit-${t.id}`}
                          onClick={() => {
                            setEditTasa({ id: t.id, tasas_number: t.tasas_number || "", presented_date: t.presented_date || "" });
                            setTasaId(t.id);
                          }}
                          className="p-1 rounded hover:bg-neutral-100 text-neutral-500"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button" title="Eliminar tasa (pide PIN maestro)"
                          data-testid={`wizard-tasa-delete-${t.id}`}
                          onClick={() => deleteTasa(t)}
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))}

              {editTasa && (
                <div className="rounded-lg border-2 border-dashed border-neutral-300 bg-white p-2 space-y-2" data-testid="wizard-tasa-edit-form">
                  <input
                    type="text" inputMode="numeric" value={editTasa.tasas_number}
                    onChange={(e) => setEditTasa((s) => ({ ...s, tasas_number: e.target.value }))}
                    data-testid="wizard-tasa-edit-number"
                    className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300 font-mono"
                  />
                  <input
                    type="date" value={editTasa.presented_date}
                    onChange={(e) => setEditTasa((s) => ({ ...s, presented_date: e.target.value }))}
                    data-testid="wizard-tasa-edit-date"
                    className="w-full py-1.5 px-2 text-sm rounded-md border border-neutral-300"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button" onClick={saveTasaEdit} disabled={savingTasa}
                      data-testid="wizard-tasa-edit-save"
                      className="flex-1 py-1.5 text-xs font-semibold rounded-md bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60"
                    >
                      Guardar cambios
                    </button>
                    <button
                      type="button" onClick={() => setEditTasa(null)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md border border-neutral-300"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {student?.id && (
                <>
                  <div className="border-t border-neutral-200/70" />
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
                    Historial de exámenes
                  </div>
                  <ExamHistoryPanel
                    studentId={student.id}
                    tasas={tasas.filter((t) => t.id)}
                    compact
                    onChanged={loadTasas}
                  />
                </>
              )}
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Observaciones</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                className="mt-1 w-full py-2 px-3 rounded-lg border border-neutral-300 text-sm" />
            </div>

            {/* Registro histórico / Traslado — sólo teórico */}
            {kind === "teorico" && (
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="historic-section">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Registro histórico / Traslado</div>
                {student?.theory_history?.approved_date && (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2 text-[12px] text-emerald-800">
                    Ya existe un registro histórico: teórico aprobado el <b>{student.theory_history.approved_date}</b>
                    {student.theory_history.center_name && <> · {student.theory_history.center_name}</>}
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={historic.enabled}
                    onChange={(e) => setHistoric((s) => ({ ...s, enabled: e.target.checked }))}
                    data-testid="historic-checkbox"
                    className="h-4 w-4 mt-0.5 accent-orange-500" />
                  <span>
                    Registrar teórico aprobado previo al sistema
                    <span className="block text-[11px] text-neutral-500">
                      Para migraciones y traslados: marca el teórico como aprobado sin crear convocatorias ni consumos retroactivos.
                    </span>
                  </span>
                </label>
                {historic.enabled && (
                  <div className="space-y-2 pt-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-neutral-500">Fecha aprobación teórico *</label>
                        <input type="date" value={historic.approved_date}
                          onChange={(e) => setHistoric((s) => ({ ...s, approved_date: e.target.value }))}
                          data-testid="historic-date"
                          className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                      </div>
                      <div>
                        <label className="text-[10px] text-neutral-500">Centro de examen (opcional)</label>
                        <input type="text" value={historic.center_name}
                          onChange={(e) => setHistoric((s) => ({ ...s, center_name: e.target.value }))}
                          data-testid="historic-center"
                          className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-neutral-500">Nº expediente DGT (opcional)</label>
                      <input type="text" value={historic.expediente_dgt}
                        onChange={(e) => setHistoric((s) => ({ ...s, expediente_dgt: e.target.value }))}
                        data-testid="historic-expediente"
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-neutral-500">Observaciones</label>
                      <textarea rows={2} value={historic.notes}
                        onChange={(e) => setHistoric((s) => ({ ...s, notes: e.target.value }))}
                        data-testid="historic-notes"
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* PASO 2 */}
        {step === 1 && (
          <div className="space-y-3 pt-2">
            <CatalogPicker label="Centro de examen" items={centers} valueId={centerId}
              onSelectId={setCenterId} onCreate={createCenter} showPhoto
              testid="wizard-center" />
            {kind === "practico" && (
              <>
                <CatalogPicker label="Punto de encuentro (obligatorio)" items={meetings} valueId={meetingId}
                  onSelectId={setMeetingId} onCreate={createMeeting}
                  testid="wizard-meeting" />
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">Bloqueo de agenda</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-neutral-500">Desde</label>
                      <input type="time" value={form.practico_block_start}
                        onChange={(e) => setForm((s) => ({ ...s, practico_block_start: e.target.value }))}
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-neutral-500">Hasta</label>
                      <input type="time" value={form.practico_block_end}
                        onChange={(e) => setForm((s) => ({ ...s, practico_block_end: e.target.value }))}
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-neutral-500">Profesor</label>
                      <select value={form.teacher_initials} onChange={(e) => setForm((s) => ({ ...s, teacher_initials: e.target.value }))}
                        data-testid="wizard-teacher"
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">— Ninguno —</option>
                        {teachers.map((t) => (
                          <option key={t.id} value={teacherInitials(t.name)}>{teacherInitials(t.name)} · {t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-neutral-500">Vehículo</label>
                      <select value={form.vehicle_id} onChange={(e) => setForm((s) => ({ ...s, vehicle_id: e.target.value }))}
                        className="w-full py-1.5 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">— Ninguno —</option>
                        {vehicles.map((v) => (
                          <option key={v.id} value={v.id}>{v.plate} · {v.model || ""}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* PASO 3 */}
        {step === 2 && (
          <div className="space-y-3 pt-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-bold text-emerald-900 mb-2">
                <CheckCircle2 className="h-4 w-4" /> Revisión final
              </div>
              <div className="grid grid-cols-2 gap-1 text-[13px]">
                <div><span className="text-neutral-500">Tipo:</span> <b>{kind === "practico" ? "PRÁCTICO" : "TEÓRICO"}</b></div>
                <div><span className="text-neutral-500">Fecha:</span> <b>{form.date}</b> {form.time}</div>
                <div className="col-span-2"><span className="text-neutral-500">Centro:</span> {centers.find((x) => x.id === centerId)?.name || "—"}</div>
                {kind === "practico" && (
                  <>
                    <div className="col-span-2"><span className="text-neutral-500">Punto encuentro:</span> {meetings.find((x) => x.id === meetingId)?.name || "—"} {form.hora_punto_encuentro && `· ${form.hora_punto_encuentro}`}</div>
                    <div><span className="text-neutral-500">Bloqueo:</span> {form.practico_block_start} – {form.practico_block_end}</div>
                    <div><span className="text-neutral-500">Profesor/Vehículo:</span> {form.teacher_initials || "—"} / {vehicles.find((v) => v.id === form.vehicle_id)?.plate || "—"}</div>
                  </>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 p-3 bg-neutral-50">
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-1">Vista previa del mensaje WhatsApp</div>
              <pre className="whitespace-pre-wrap font-mono text-[12px] text-neutral-800">{preview || "El mensaje se generará al guardar…"}</pre>
              <div className="text-[10px] text-neutral-500 mt-1">Podrás editar el texto antes de enviarlo.</div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-3 mt-2 border-t border-neutral-200">
          <button type="button"
            onClick={step === 0 ? onClose : () => setStep(step - 1)}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50">
            {step === 0 ? "Cancelar" : <><ChevronLeft className="h-4 w-4" /> Atrás</>}
          </button>
          {step === 0 && kind === "teorico" && historic.enabled ? (
            <button type="button" onClick={submitHistoric} disabled={submitting || !historic.approved_date}
              data-testid="historic-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</> : <><CheckCircle2 className="h-4 w-4" /> Guardar registro histórico</>}
            </button>
          ) : step < 2 ? (
            <button type="button"
              disabled={(step === 0 && !step1Valid) || (step === 1 && !step2Valid)}
              onClick={() => setStep(step + 1)}
              data-testid="wizard-next"
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50">
              Siguiente <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting}
              data-testid="wizard-submit"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Programando…</> : <><CheckCircle2 className="h-4 w-4" /> Programar convocatoria</>}
            </button>
          )}
        </div>
      </DialogContent>
      <ExamRequestLogisticsDialog
        request={tasaDetail}
        open={!!tasaDetail}
        onClose={() => { setTasaDetail(null); loadTasas(); }}
      />
    </Dialog>
  );
}
